import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Share2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { getBlogPostBySlug } from '../data/blogPosts';

const parseMarkdown = (text) => {
  if (!text) return '';
  // (Mantive sua lógica de parse, que está boa, apenas adicionei classes de estilo)
  let html = text
    .replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold text-slate-100 mt-12 mb-6 font-outfit">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold text-slate-100 mt-16 mb-8 font-outfit pb-4 border-b border-slate-800">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-4xl font-bold text-slate-100 mt-16 mb-8">$1</h1>')
    .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong class="text-cyan-200"><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong class="text-slate-100 font-semibold">$1</strong>')
    .replace(/```([\s\S]*?)```/gim, '<pre class="bg-slate-950 border border-slate-800 rounded-xl p-6 overflow-x-auto my-8 shadow-inner"><code class="text-sm font-mono text-cyan-300">$1</code></pre>')
    .replace(/`(.*?)`/gim, '<code class="bg-slate-800 px-1.5 py-0.5 rounded text-sm text-cyan-400 font-mono">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline decoration-cyan-500/30 underline-offset-4 transition-colors">$1</a>')
    .replace(/^\- (.*$)/gim, '<li class="ml-4 text-slate-300 mb-2 list-disc marker:text-cyan-500 pl-2">$1</li>')
    .replace(/\n\n/gim, '</p><p class="text-slate-300 text-lg leading-relaxed mb-6">')
    .replace(/\n/gim, '<br />');

  if (!html.startsWith('<')) {
    html = `<p class="text-slate-300 text-lg leading-relaxed mb-6">${html}</p>`;
  }
  return html;
};

export default function BlogPost() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const post = getBlogPostBySlug(slug, language);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-100 mb-4">{t.blog.postNotFound}</h1>
          <Button onClick={() => navigate(`/${language}/blog`)}>{t.common.backToBlog}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-slate-950">
      {/* Progress Bar (Visual flair) */}
      <div className="fixed top-0 left-0 h-1 bg-gradient-to-r from-cyan-500 to-emerald-500 w-full z-50 opacity-20" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        
        {/* Navigation */}
        <div className="flex justify-between items-center mb-12">
            <Link to={`/${language}/blog`} className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                {t.common.backToBlog}
            </Link>
        </div>

        {/* Article Header */}
        <header className="mb-12 lg:mb-16">
            <div className="flex items-center gap-4 text-cyan-400 font-mono text-sm mb-6">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(post.published_at).toLocaleDateString()}
                </div>
            </div>

            <h1 className="font-outfit text-4xl lg:text-6xl font-bold text-slate-100 mb-8 leading-tight">
                {post.title}
            </h1>

            <div className="flex flex-wrap gap-2">
                {post.tags?.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-slate-900 border border-slate-800 text-slate-400 text-xs rounded-full uppercase tracking-wider">
                    <Tag className="w-3 h-3" />
                    {tag}
                </span>
                ))}
            </div>
        </header>

        {/* Content */}
        <article className="prose prose-invert max-w-none">
             <div dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content) }} />
        </article>

        {/* Footer / CTA */}
        <div className="mt-20 pt-10 border-t border-slate-800">
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-8 lg:p-12 text-center">
                <h3 className="font-outfit text-2xl font-bold text-slate-100 mb-4">
                    {language === 'pt' ? 'Gostou do conteúdo?' : 'Did you like this?'}
                </h3>
                <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                    {language === 'pt' 
                     ? 'Aplicamos essas mesmas estratégias para recuperar receita nos nossos clientes. Vamos conversar?' 
                     : 'We apply these strategies to recover revenue for our clients. Let\'s talk?'}
                </p>
                <Link to={`/${language}/contact`}>
                    <Button className="bg-slate-100 text-slate-950 hover:bg-white font-bold rounded-full px-8 py-6">
                        {language === 'pt' ? 'Falar com o time' : 'Talk to the team'}
                    </Button>
                </Link>
            </div>
        </div>

      </div>
    </div>
  );
}
